import { type FC, useMemo } from 'react';
import { type SampleDefinitionMetadataV1 } from '@/model/SamplePlan.model';
import { useSamplePlanTableContext } from '@/components/Samples/SamplePlanTable/SamplePlanTableProvider';
import { DateRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import { formatNumber } from '@/helpers';
import { _isNotBlank, _notNil, safelyDecodeURIComponent } from '@/littledash';
import { type Nullable } from '@/model/Common.model';

const MultiSelectValue: FC<{ value: Nullable<string> }> = ({ value }) => {
  return useMemo(() => {
    if (_isNotBlank(value)) {
      try {
        const parsedValue = JSON.parse(value);
        return Array.isArray(parsedValue) ? parsedValue.map((v) => safelyDecodeURIComponent(v)).join(', ') : '-';
      } catch (e) {
        // intentionally empty
      }
    }
    return '-';
  }, [value]);
};

export const MetadataCell: FC<SampleDefinitionMetadataV1> = ({ type, value }) => {
  const { metadata_glossary } = useSamplePlanTableContext();
  const glossary = metadata_glossary.get(type);
  switch (glossary?.field_type) {
    case 'date':
      return (
        <div data-test-component="MetadataCell" data-test-element="date">
          <DateRenderer value={value} defaultResponse="-" />
        </div>
      );
    case 'numeric':
      return (
        <div data-test-component="MetadataCell" data-test-element="numeric">
          {_notNil(value) ? formatNumber(value) : '-'}
        </div>
      );
    case 'multi_select':
      return (
        <div data-test-component="MetadataCell" data-test-element="multi_select">
          <MultiSelectValue value={value} />
        </div>
      );
    default:
      return (
        <div data-test-component="MetadataCell" data-test-element="default">
          {value ?? '-'}
        </div>
      );
  }
};
